import React from 'react';
import Chart from 'react-apexcharts';
import { Empty, Spin } from 'antd';
import { formateNumberWithCommas } from '../../utils';
import { useThemeModeContext } from 'contexts/theme-mode-context';
import clsx from 'clsx';

const MultiLineGraph = ({
  data = [],
  xField = '',
  yField = '',
  field = '', // series key
  height = 400,
  width,
  smooth = true,
  connectNullValues = true,
  showLabelsOnLine = false,
  labelFormatter = (val = '') => val?.toLocaleString(),
  xAxisLabelFormatter = (val = '') => val?.toLocaleString(),
  yAxisLabelFormatter = (val = '') => val?.toLocaleString(),
  showLegend = true,
  legendPosition = 'top',
  customColors = false,
  colorsArray = [],
  tooltipValueFormater = (value) => formateNumberWithCommas(value),
  showCustomToolTip = false,
  customToolTipContent = () => {},
  lineStyleProps = {},
  pending = false,
}) => {
  const { themeMode = 'light' } = useThemeModeContext();

  if (pending) return <Spin style={{ height: 396 }} className="center-content" />;

  if (!data?.length)
    return (
      <div>
        <Empty description={<span>No Data Found</span>} style={{ height: 396 }} className="center-content" />
      </div>
    );

  // 👉 Transform flat data into Apex series format
  const allDates = [...new Set(data.map((item) => item[xField]))].sort((a, b) => new Date(a) - new Date(b));

  const grouped = data.reduce((acc, item) => {
    const key = item[field];
    if (!acc[key]) acc[key] = {};
    acc[key][item[xField]] = item[yField];
    return acc;
  }, {});

  const series = Object.keys(grouped).map((key) => ({
    name: key,
    data: allDates.map((date) => ({
      x: date,
      y: grouped[key][date] ?? 0,
    })),
  }));

  const options = {
    chart: {
      type: 'line',
      height,
      foreColor: themeMode === 'dark' ? '#ccc' : '#333',
      toolbar: { show: false },
      zoom: { enabled: false },
    },

    stroke: {
      curve: smooth ? 'smooth' : 'straight',
      width: lineStyleProps?.lineWidth || 2,
    },

    dataLabels: {
      enabled: showLabelsOnLine,
      formatter: (val) => labelFormatter(val),
    },

    xaxis: {
      type: 'category',
      labels: {
        formatter: (val) => xAxisLabelFormatter(val),
      },
    },

    yaxis: {
      labels: {
        formatter: (val) => yAxisLabelFormatter(val),
      },
      min: 0,
    },

    legend: {
      show: showLegend,
      position: legendPosition.includes('top') ? 'top' : legendPosition.includes('bottom') ? 'bottom' : 'right',
    },

    tooltip: showCustomToolTip
      ? {
          custom: ({ series, seriesIndex, dataPointIndex, w }) =>
            customToolTipContent(w.globals.labels[dataPointIndex], series[seriesIndex][dataPointIndex]),
        }
      : {
          theme: themeMode === 'dark' ? 'dark' : 'light',
          style: {
            fontSize: '12px',
          },

          y: {
            formatter: (val) => tooltipValueFormater(val),
          },
        },

    colors: customColors && colorsArray.length ? colorsArray : undefined,

    markers: {
      size: 4,
    },

    noData: {
      text: 'No Data Found',
    },
  };

  return (
    <div className={clsx('apexchart', data.length > 100 ? 'threecol' : data.length > 50 ? 'twocol' : '')}>
      <Chart options={options} series={series} type="line" height={height} width={width} />
    </div>
  );
};

export default MultiLineGraph;
